/* @flow */
/* eslint-env es6,browser */

import _multiidpbase from './multiidpbase';
import {
  IDP_LIVERAMP,
  USER_ID_LIVERAMP,
  USER_ID_GOOGLEPAIRID
} from './idpconstants';
import {
  getFromStore,
  saveToStore,
  getStoredValueTS
} from '../utils/storageutils';
import getCVars from '../utils/gputils';
import { isFetchSupported, fetchJSON } from '../utils/fetch';
import { getCCPA } from '../utils/ccpautils';
import { isJSONSupported } from '../utils/browserutil';
import { isArray } from '../utils/coreutils';
import { logError, logDebug } from '../utils/logger';

// const DEFAULT_REFRESH_INTERVAL = 30; // in min for LiveRamp
const LIVERAMP_SOURCE_ID = 19;

const INTERNAL_MAP = {
  [USER_ID_LIVERAMP]: {
    cookieName: '_lr_env',
    respSource: 'envelopeLiveramp'
  },
  [USER_ID_GOOGLEPAIRID]: {
    cookieName: '_lr_pairId',
    respSource: 'pairIds'
  }
};

/**
 * Reference
 * https://docs.authenticated-traffic-solution.com/reference/retrieving-envelopes
 * https://docs.authenticated-traffic-solution.com/docs/api-only-implementation
 */

/**
 * @description Implement functionality generating LiveRamp ID
 * @module liverampid
 */
class _liverampidp extends _multiidpbase {
  _idRequested: boolean;

  constructor(name: string) {
    super(name);
    this._idRequested = false; // to avoid requesting repeatedly for adblock scenario
  }

  _isSupported(): boolean {
    return super._isSupported() && isJSONSupported() && isFetchSupported();
  }

  _loadFromCache(): boolean {
    // first try cache/cookie locally
    Object.keys(INTERNAL_MAP).forEach(idpKey => {
      this._loadFromCookie(idpKey);
    });
    if (this._refreshInterval > 0) {
      this._idUpdateTime =
        getStoredValueTS(
          INTERNAL_MAP[USER_ID_LIVERAMP].cookieName,
          this._refreshInterval * 60,
          this._storage
        ) || this._idUpdateTime;
    }
    return Object.keys(this._cachedObj).length > 0;
  }

  _loadFromCookie(idpKey: string = '') {
    // if it's already present in local cache, return it.
    let cachedValue = this._cachedObj[idpKey] || '';
    if (cachedValue.length > 0) {
      return;
    }

    const { cookieName } = INTERNAL_MAP[idpKey];
    const cookieVal = getFromStore(cookieName, this._storage);
    if (cookieVal.length === 0) {
      return;
    }
    // eslint-disable-next-line compat/compat
    const { envelope = [] } = JSON.parse(atob(cookieVal));
    cachedValue = envelope;
    // for liveramp, get the first value in the array and return
    // google pairId, we need to return as it is.
    if (idpKey === USER_ID_LIVERAMP) {
      cachedValue = cachedValue[0] || '';
    }

    logDebug(
      `${this._name} - Retrieved from cookie ${idpKey} - ${envelope.join(',')}`
    );
    // store it in local cache
    this._cachedObj[idpKey] = cachedValue;
  }

  _shouldRefresh(): boolean {
    // check for expiry/refresh
    return (
      super._shouldRefresh() ||
      getStoredValueTS(
        INTERNAL_MAP[USER_ID_LIVERAMP].cookieName,
        this._refreshInterval * 60,
        this._storage
      ) === 0
    );
  }

  _updateCachedDataAndCookie(idpKey: string = '', val: string = '') {
    if (val.length === 0 || idpKey.length === 0) {
      // nothing to store here.
      return;
    }

    const { cookieName } = INTERNAL_MAP[idpKey];
    let envelope = '';
    let cookieVal = '';
    let respVal = val;
    if (!isArray(respVal)) {
      respVal = [respVal];
    }
    envelope = respVal;
    // storing the cookie can vary based on the ID.
    if (idpKey === USER_ID_LIVERAMP) {
      const [liverampVal = ''] = respVal;
      this._cachedObj[USER_ID_LIVERAMP] = liverampVal;
    } else if (idpKey === USER_ID_GOOGLEPAIRID) {
      // decode it and parse to get an array
      let decoded = '';
      let pairIds = [];
      // ignore decoding exception
      try {
        decoded = atob(val) || '';
        if (decoded.length > 0) {
          pairIds = JSON.parse(decoded);
        }
      } catch {
        logError(`Google pair id parsing error`);
      }
      if (pairIds.length === 0) {
        // something wrong and nothing to store here.
        return;
      }
      this._cachedObj[USER_ID_GOOGLEPAIRID] = pairIds;
      envelope = pairIds;
    }
    // we always store envelope as array in the cookie
    if (!isArray(envelope)) {
      envelope = [envelope];
    }
    // build object, base64 and store it in cookie
    const cacheObject = {
      envelope
    };
    logDebug(
      `${
        this._name
        // $FlowFixMe
      } - Encrypt and store CookieName: ${cookieName} ${idpKey} - ${envelope.join(
        ','
      )}`
    );
    // eslint-disable-next-line compat/compat
    cookieVal = btoa(JSON.stringify(cacheObject));
    // store it in cookie for future
    saveToStore(
      {
        name: cookieName,
        value: cookieVal,
        doc: this._doc,
        expires: this._nExpireDays * 24 * 60 * 60
      },
      this._refreshInterval > 0,
      this._storage
    );
  }

  _processResponse({ envelopes = [] }: LREnvelopeResponseWrapper = {}) {
    // Only for enabled providers, we need to try and store the data
    // from the server response.
    const internalMapPairs = Object.entries(INTERNAL_MAP).filter(([k]) =>
      this._enabledProviders.includes(k)
    );
    envelopes.forEach(({ source, value }) => {
      // $FlowFixMe
      const found = internalMapPairs.find(([, v]) => source === v.respSource);
      if (found === undefined) {
        // new envelope we don't support yet
        return;
      }
      const [idpKey] = found;
      this._updateCachedDataAndCookie(idpKey, value);
    });
  }

  _refreshID(): void {
    // do we have valid config to send request
    if (
      this._url.length === 0 ||
      this._refreshUrl.length === 0 ||
      this._pid.length === 0 ||
      this._idRequested
    ) {
      return;
    }

    // use appropriate URL to refresh when needed
    let URL = this._url;
    const hashParamPairs = [`pid=${this._pid}`];
    // add consent to param
    const ccpaValue = getCCPA();
    if (ccpaValue.length > 0) {
      hashParamPairs.push(`ct=3&cv=${ccpaValue}`);
    }

    // get existing liveramp value
    let bRefresh = false;
    const envelope = this._cachedObj[USER_ID_LIVERAMP] || '';
    if (envelope.length > 0) {
      // we already have one envelope. Time to refresh it.
      URL = this._refreshUrl;
      // append the param too.
      hashParamPairs.push(`it=${LIVERAMP_SOURCE_ID}&iv=${envelope}`);
      bRefresh = true;
    }
    if (!bRefresh) {
      // get customer's hash values
      const cvars = getCVars();
      const {
        _uh: sha1Email = '',
        _uh4: md5Email = '',
        _uh6: sha256Email = ''
      } = cvars;
      // all email hashes should be sent with it=4&iv=<value>
      hashParamPairs.push(
        ...[sha1Email, md5Email, sha256Email]
          .filter((v: string) => v.length > 0)
          .map((v: string) => `it=4&iv=${v}`)
      );
    }

    URL = `${URL}?${hashParamPairs.join('&')}`;
    logDebug(
      `${this._name} - ${bRefresh ? 'Refreshing' : 'Fetching'} Liveramp ID`
    );
    this._idRequested = true;
    // eslint-disable-next-line promise/catch-or-return
    fetchJSON(
      URL,
      { data: {}, logPrefix: IDP_LIVERAMP, timeout: 2000 },
      { credentials: 'include', mode: 'cors' }
    )
      .then((res: LREnvelopeResponseWrapper = {}) => {
        this._processResponse(res);
        return res;
      })
      .finally(() => {
        this._idRequested = false;
      });
  }
}
const liverampidp: _liverampidp = new _liverampidp(IDP_LIVERAMP);

export default liverampidp;
